import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Provider } from "react-redux";
import { store } from "@/store";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./AppRoutes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useQueryConfig } from "@/hooks/useQueryConfig";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AutoTranslate } from "@/components/translation/AutoTranslate";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { CookieConsent } from "@/components/CookieConsent";
import { RouteScroll } from "@/components/layout/RouteScroll";
import { Helmet } from "react-helmet";
import { ErrorBoundary } from "@/components/errors/ErrorBoundary";

function App() {
  const [paypalClientId, setPaypalClientId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const queryConfig = useQueryConfig();

  // Memoize the QueryClient so it isn't recreated on every render
  // (re-creation wipes the cache and triggers a refetch storm).
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: queryConfig }),
    // queryConfig is a stable shape; intentionally created once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const fetchPayPalClientId = async () => {
      try {
        // Served by the server (get_secret is reserved to service_role).
        const { data, error } = await supabase.functions.invoke('get-public-config');
        if (error) {
          console.error('Erreur lors de la récupération de la configuration:', error);
          return;
        }
        if (data?.paypalClientId) setPaypalClientId(data.paypalClientId);
      } catch (error) {
        console.error('Erreur lors de la récupération du secret:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPayPalClientId();
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <Provider store={store}>
      <LanguageProvider>
        <CurrencyProvider>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
            <BrowserRouter basename="/">
              <Helmet><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap" /></Helmet>
              <RouteScroll />
              <PayPalScriptProvider options={{
                clientId: paypalClientId || "test",
                currency: "EUR"
              }}>
                <AppRoutes />
                <AutoTranslate />
                <CookieConsent />
                <Toaster />
              </PayPalScriptProvider>
            </BrowserRouter>
            </ErrorBoundary>
          </QueryClientProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </Provider>
  );
}

export default App;
